/**
 * Centralized Permission Configuration
 *
 * This file is the single source of truth for all permission-related configurations
 * in the frontend application. It defines:
 * - Route permissions (which permissions are required for each route)
 * - Section permissions (permissions for tabs and actions within sections)
 * - Sidebar permissions (which items are visible based on permissions)
 */

import { Permission } from 'features/roles';

// ============================================================================
// Types
// ============================================================================

export interface RoutePermissionConfig {
  /** Permission(s) required to access this route */
  permission?: Permission | Permission[];
  /** How to check multiple permissions: 'ALL' (default) or 'ANY' */
  permissionMode?: 'ALL' | 'ANY';
  /** If true, only admin/superadmin can access */
  adminOnly?: boolean;
}

export interface TabActionConfig {
  /** Permissions required to view this tab */
  view: Permission[];
  /** Permissions required to edit content in this tab */
  edit?: Permission[];
  /** Permissions required to create new items */
  create?: Permission[];
  /** Permissions required to delete items */
  delete?: Permission[];
  /** Additional custom actions with their required permissions */
  [action: string]: Permission[] | undefined;
}

export interface SectionConfig {
  /** Base permission to access this section */
  basePermission: Permission | Permission[] | null;
  /** If true, only admin/superadmin can access */
  adminOnly?: boolean;
  /** Tab configurations with their permissions */
  tabs: Record<string, TabActionConfig>;
  /** Action configurations with their required permissions */
  actions?: Record<string, Permission[]>;
}

/**
 * Maps sidebar item IDs to their corresponding route paths.
 * This allows sidebar permissions to be derived from ROUTE_PERMISSIONS,
 * since the sidebar is just quick navigation to routes.
 */
export const SIDEBAR_ROUTE_MAP: Record<string, string> = {
  dashboard: '/dashboard',
  tasks: '/tasks',
  projects: '/projects',
  'work-orders': '/work-orders',
  leads: '/leads',
  location: '/location',
  schedule: '/schedule',
  inventory: '/inventory',
  accounting: '/accounting',
  company: '/company',
  'budget-templates': '/budget-templates',
  'quote-templates': '/quote-templates',
};

// ============================================================================
// Route Permissions Configuration
// ============================================================================

export const ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  // Dashboard
  '/dashboard': {
    permission: [Permission.VIEW_PROJECT_BUDGETS, Permission.VIEW_EXPENSES],
    permissionMode: 'ALL',
  },

  // Tasks
  '/tasks': {
    permission: Permission.VIEW_ALL_TASKS,
  },

  // Projects
  '/projects': {
    permission: Permission.VIEW_PROJECTS,
  },
  '/projects/new': {
    permission: [Permission.CREATE_PROJECTS, Permission.EDIT_PROJECT_BUDGETS, Permission.EDIT_TEMPLATES],
    permissionMode: 'ALL',
  },
  '/projects/:id': {
    permission: Permission.VIEW_PROJECTS,
  },

  // Project Templates
  '/projects/templates': {
    permission: Permission.VIEW_TEMPLATES,
  },
  '/projects/templates/new': {
    permission: Permission.EDIT_TEMPLATES,
  },
  '/projects/templates/:id': {
    permission: Permission.VIEW_TEMPLATES,
  },
  '/projects/templates/:id/edit': {
    permission: Permission.EDIT_TEMPLATES,
  },

  // Work Orders
  '/work-orders': {
    permission: Permission.VIEW_WORK_ORDERS,
  },
  '/work-orders/new': {
    permission: Permission.CREATE_WORK_ORDERS,
  },
  '/work-orders/:id': {
    permission: Permission.VIEW_WORK_ORDERS,
  },
  '/work-orders/templates': {
    permission: Permission.VIEW_TEMPLATES,
  },

  // Leads
  '/leads': {
    permission: Permission.VIEW_LEADS,
  },
  '/leads/:id': {
    permission: Permission.VIEW_LEADS,
  },
  '/leads/forms': {
    adminOnly: true,
  },
  '/leads/forms/:id': {
    adminOnly: true,
  },

  // Inventory
  '/inventory': {
    permission: Permission.VIEW_INVENTORY,
  },

  // Accounting
  '/accounting': {
    permission: Permission.VIEW_FINANCIAL_REPORTS,
  },

  // Budget Templates
  '/budget-templates': {
    permission: Permission.VIEW_PROJECT_BUDGETS,
  },
  '/budget-templates/new': {
    permission: Permission.EDIT_PROJECT_BUDGETS,
  },
  '/budget-templates/:id': {
    permission: Permission.VIEW_PROJECT_BUDGETS,
  },

  // Quote Templates
  '/quote-templates': {
    permission: Permission.VIEW_PROPOSALS,
  },
  '/quote-templates/new': {
    permission: Permission.CREATE_PROPOSALS,
  },
  '/quote-templates/:id': {
    permission: Permission.VIEW_PROPOSALS,
  },

  // Schedule
  '/schedule': {
    permission: Permission.VIEW_SCHEDULE,
  },

  // Location
  '/location': {
    permission: Permission.VIEW_CLIENTS,
  },

  // Company (admin only)
  '/company': {
    adminOnly: true,
  },
  '/team': {
    adminOnly: true,
  },
  '/documents': {
    adminOnly: true,
  },
};

// ============================================================================
// Section Permissions Configuration
// ============================================================================

export const SECTION_PERMISSIONS: Record<string, SectionConfig> = {
  projects: {
    basePermission: Permission.VIEW_PROJECTS,
    tabs: {
      overview: {
        view: [Permission.VIEW_PROJECTS],
        edit: [Permission.EDIT_PROJECTS],
      },
      tasks: {
        view: [Permission.VIEW_PROJECTS],
        edit: [Permission.EDIT_PROJECTS],
        assign: [Permission.ASSIGN_TASKS],
      },
      budget: {
        view: [Permission.EDIT_PROJECT_BUDGETS],
        edit: [Permission.EDIT_PROJECT_BUDGETS],
      },
      invoices: {
        view: [Permission.VIEW_PROJECTS],
        create: [Permission.CREATE_INVOICES],
        edit: [Permission.EDIT_INVOICES],
        delete: [Permission.EDIT_INVOICES],
        recordPayment: [Permission.EDIT_INVOICES],
      },
      expenses: {
        view: [Permission.VIEW_EXPENSES],
        create: [Permission.EDIT_EXPENSES],
        edit: [Permission.EDIT_EXPENSES],
        delete: [Permission.EDIT_EXPENSES],
      },
      materials: {
        view: [Permission.VIEW_INVENTORY],
        createPickList: [Permission.EDIT_INVENTORY],
        fulfillPickList: [Permission.EDIT_INVENTORY],
        cancelPickList: [Permission.EDIT_INVENTORY],
      },
      workOrders: {
        view: [Permission.VIEW_WORK_ORDERS],
        create: [Permission.CREATE_WORK_ORDERS],
      },
      documents: {
        view: [Permission.VIEW_PROJECTS],
        upload: [Permission.EDIT_PROJECTS],
        delete: [Permission.EDIT_PROJECTS],
        share: [Permission.EDIT_PROJECTS],
      },
      contacts: {
        view: [Permission.VIEW_PROJECTS],
        edit: [Permission.EDIT_PROJECTS],
      },
      notes: {
        view: [Permission.VIEW_PROJECTS],
        create: [Permission.EDIT_PROJECTS],
      },
      partners: {
        view: [Permission.VIEW_PARTNERS],
        assign: [Permission.MANAGE_PARTNERS],
        unassign: [Permission.MANAGE_PARTNERS],
      },
      schedule: {
        view: [Permission.VIEW_SCHEDULE],
        edit: [Permission.EDIT_SCHEDULE],
        create: [Permission.EDIT_SCHEDULE],
        delete: [Permission.EDIT_SCHEDULE],
      },
    },
    actions: {
      create: [Permission.CREATE_PROJECTS, Permission.EDIT_PROJECT_BUDGETS, Permission.EDIT_TEMPLATES],
      edit: [Permission.EDIT_PROJECTS],
      delete: [Permission.DELETE_PROJECTS],
    },
  },

  workOrders: {
    basePermission: Permission.VIEW_WORK_ORDERS,
    tabs: {
      details: {
        view: [Permission.VIEW_WORK_ORDERS],
        edit: [Permission.EDIT_WORK_ORDERS],
      },
      comments: {
        view: [Permission.VIEW_WORK_ORDERS],
        post: [Permission.EDIT_WORK_ORDERS],
      },
      documents: {
        view: [Permission.VIEW_WORK_ORDERS],
        upload: [Permission.EDIT_WORK_ORDERS],
        delete: [Permission.EDIT_WORK_ORDERS],
      },
      schedule: {
        view: [Permission.VIEW_WORK_ORDERS],
        edit: [Permission.EDIT_WORK_ORDERS],
      },
      materials: {
        view: [Permission.VIEW_INVENTORY],
        createPickList: [Permission.EDIT_INVENTORY],
        fulfillPickList: [Permission.EDIT_INVENTORY],
        cancelPickList: [Permission.EDIT_INVENTORY],
      },
      location: {
        view: [Permission.VIEW_WORK_ORDERS],
        edit: [Permission.EDIT_WORK_ORDERS],
      },
    },
    actions: {
      create: [Permission.CREATE_WORK_ORDERS],
      edit: [Permission.EDIT_WORK_ORDERS],
      delete: [Permission.DELETE_WORK_ORDERS],
      assignTechnician: [Permission.ASSIGN_TASKS],
      unassignTechnician: [Permission.ASSIGN_TASKS],
      assignToProject: [Permission.EDIT_WORK_ORDERS],
      generatePdf: [Permission.VIEW_WORK_ORDERS],
    },
  },

  leads: {
    basePermission: Permission.VIEW_LEADS,
    tabs: {
      details: {
        view: [Permission.VIEW_LEADS],
        edit: [Permission.EDIT_LEADS],
      },
      timeline: {
        view: [Permission.VIEW_LEADS],
      },
      documents: {
        view: [Permission.VIEW_LEADS],
        upload: [Permission.EDIT_LEADS],
        delete: [Permission.EDIT_LEADS],
      },
      budget: {
        view: [Permission.VIEW_LEADS],
        edit: [Permission.EDIT_LEADS],
      },
      quotes: {
        view: [Permission.VIEW_PROPOSALS],
        create: [Permission.CREATE_PROPOSALS],
        edit: [Permission.EDIT_PROPOSALS],
        delete: [Permission.DELETE_PROPOSALS],
        setPrimary: [Permission.EDIT_PROPOSALS],
      },
      contacts: {
        view: [Permission.VIEW_LEADS],
        add: [Permission.EDIT_LEADS],
        edit: [Permission.EDIT_LEADS],
        remove: [Permission.EDIT_LEADS],
      },
    },
    actions: {
      create: [Permission.CREATE_LEADS],
      edit: [Permission.EDIT_LEADS],
      delete: [Permission.DELETE_LEADS],
      assign: [Permission.EDIT_LEADS],
      updateStatus: [Permission.EDIT_LEADS],
      convertToProject: [Permission.CREATE_PROJECTS],
      convertToProjectAdvanced: [Permission.CREATE_PROJECTS, Permission.EDIT_PROJECT_BUDGETS, Permission.EDIT_TEMPLATES],
    },
  },

  inventory: {
    basePermission: Permission.VIEW_INVENTORY,
    tabs: {
      overview: {
        view: [Permission.VIEW_INVENTORY],
      },
      itemGroups: {
        view: [Permission.VIEW_INVENTORY],
        create: [Permission.EDIT_INVENTORY],
        edit: [Permission.EDIT_INVENTORY],
        delete: [Permission.EDIT_INVENTORY],
      },
      allItems: {
        view: [Permission.VIEW_INVENTORY],
        create: [Permission.EDIT_INVENTORY],
        edit: [Permission.EDIT_INVENTORY],
        delete: [Permission.EDIT_INVENTORY],
        manageVariants: [Permission.EDIT_INVENTORY],
        stockIn: [Permission.EDIT_INVENTORY],
        stockOut: [Permission.EDIT_INVENTORY],
        adjustStock: [Permission.EDIT_INVENTORY],
      },
      vendors: {
        view: [Permission.VIEW_INVENTORY],
        create: [Permission.EDIT_INVENTORY],
        edit: [Permission.EDIT_INVENTORY],
        delete: [Permission.EDIT_INVENTORY],
      },
      purchaseOrders: {
        view: [Permission.VIEW_INVENTORY],
        create: [Permission.EDIT_INVENTORY],
        edit: [Permission.EDIT_INVENTORY],
        delete: [Permission.EDIT_INVENTORY],
        receiveDelivery: [Permission.EDIT_INVENTORY],
        cancel: [Permission.EDIT_INVENTORY],
      },
      pickLists: {
        view: [Permission.VIEW_INVENTORY],
        create: [Permission.EDIT_INVENTORY],
        fulfill: [Permission.EDIT_INVENTORY],
        cancel: [Permission.EDIT_INVENTORY],
      },
      stockMovements: {
        view: [Permission.VIEW_INVENTORY],
        stockIn: [Permission.EDIT_INVENTORY],
        stockOut: [Permission.EDIT_INVENTORY],
        adjust: [Permission.EDIT_INVENTORY],
      },
    },
  },

  accounting: {
    basePermission: Permission.VIEW_FINANCIAL_REPORTS,
    tabs: {
      overview: {
        view: [Permission.VIEW_FINANCIAL_REPORTS],
      },
      invoices: {
        view: [Permission.VIEW_INVOICES],
        download: [Permission.VIEW_INVOICES],
      },
      expenses: {
        view: [Permission.VIEW_EXPENSES],
        create: [Permission.EDIT_EXPENSES],
        edit: [Permission.EDIT_EXPENSES],
        delete: [Permission.EDIT_EXPENSES],
      },
    },
  },

  budgetTemplates: {
    basePermission: Permission.VIEW_PROJECT_BUDGETS,
    tabs: {},
    actions: {
      view: [Permission.VIEW_PROJECT_BUDGETS],
      create: [Permission.EDIT_PROJECT_BUDGETS],
      edit: [Permission.EDIT_PROJECT_BUDGETS],
      delete: [Permission.EDIT_PROJECT_BUDGETS],
    },
  },

  quoteTemplates: {
    basePermission: Permission.VIEW_PROPOSALS,
    tabs: {},
    actions: {
      view: [Permission.VIEW_PROPOSALS],
      create: [Permission.CREATE_PROPOSALS],
      edit: [Permission.EDIT_PROPOSALS],
      delete: [Permission.DELETE_PROPOSALS],
    },
  },

  company: {
    basePermission: null,
    adminOnly: true,
    tabs: {
      profile: {
        view: [],
        edit: [],
      },
      team: {
        view: [],
        create: [Permission.MANAGE_USERS],
        edit: [Permission.MANAGE_USERS],
        delete: [Permission.MANAGE_USERS],
      },
      roles: {
        view: [],
        create: [Permission.MANAGE_PERMISSIONS],
        edit: [Permission.MANAGE_PERMISSIONS],
        delete: [Permission.MANAGE_PERMISSIONS],
      },
      documents: {
        view: [],
        upload: [],
        delete: [],
      },
      forms: {
        view: [],
        create: [],
        edit: [],
        delete: [],
      },
      partners: {
        view: [Permission.VIEW_PARTNERS],
        invite: [Permission.INVITE_PARTNERS],
        manage: [Permission.MANAGE_PARTNERS],
      },
    },
  },

  dashboard: {
    basePermission: [Permission.VIEW_PROJECT_BUDGETS, Permission.VIEW_EXPENSES],
    tabs: {},
  },
};


